import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { ArrowRight, Sparkles, Clock, ChevronLeft } from "lucide-react";

export default function TopicDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await apiClient.get(`/seo/topic/${slug}`);
        setTopic(r.data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useSEO({
    title: topic ? `${topic.h1} — Master AI for ${topic.name} · PromptDojo` : "Topic · PromptDojo",
    description: topic?.blurb,
    canonical: `https://aipromptdojo.com/topics/${slug}`,
    ogImage: "https://aipromptdojo.com/api/og/UNKNOWN.png",
    jsonLd: topic
      ? {
          "@context": "https://schema.org",
          "@type": "Course",
          name: topic.h1,
          description: topic.blurb,
          provider: { "@type": "Organization", name: "PromptDojo", sameAs: "https://aipromptdojo.com" },
          hasCourseInstance: topic.subtopics.map((s) => ({
            "@type": "CourseInstance",
            name: s.name,
            url: `https://aipromptdojo.com/challenges/${s.challenge_slug}`,
            courseMode: "online",
          })),
        }
      : null,
  });

  if (notFound) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="font-display text-4xl">Topic not found</h1>
          <Link to="/topics" className="btn-primary mt-6 inline-flex" data-testid="topic-back-to-index">
            Back to all topics
          </Link>
        </main>
      </div>
    );
  }
  if (loading || !topic) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto p-20 text-center"><span className="spinner" /></main>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 pb-28 md:pb-14">
        <button
          onClick={() => navigate("/topics")}
          className="text-sm text-[var(--ink-3)] hover:text-[var(--ink-2)] inline-flex items-center gap-1 mb-6"
          data-testid="topic-detail-back"
        >
          <ChevronLeft size={14} /> All topics
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: topic.accent }}
          >
            <span className="text-2xl leading-none">{topic.emoji}</span>
          </div>
          <span className="kicker" style={{ color: topic.accent }}>Topic</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tight">
          {topic.h1}
        </h1>
        <p className="text-[var(--ink-2)] mt-4 leading-relaxed text-lg max-w-3xl">
          {topic.blurb}
        </p>

        {topic.bullets?.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-2">
            {topic.bullets.map((b) => (
              <li key={b} className="pill" style={{ borderColor: `${topic.accent}33`, color: "var(--ink)" }}>
                {b}
              </li>
            ))}
          </ul>
        )}

        <h2 className="font-display text-3xl mt-12 mb-5 leading-tight">
          Challenges in this path
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topic.subtopics.map((s, idx) => (
            <Link
              key={s.challenge_slug}
              to={`/challenges/${s.challenge_slug}`}
              className="brut p-5 group transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid={`subtopic-card-${s.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="kicker" style={{ color: topic.accent }}>#{idx + 1}</div>
                <div className="flex items-center gap-3 text-xs text-[var(--ink-3)]">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} /> 5 min
                  </span>
                </div>
              </div>
              <div className="font-display text-xl leading-tight">{s.name}</div>
              <div className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed line-clamp-3">{s.intro}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--accent)] group-hover:gap-2 transition-all">
                Start challenge <ArrowRight size={13} />
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div
          className="mt-14 brut-lg p-8 md:p-10 text-center"
          style={{ background: `linear-gradient(135deg, ${topic.accent}1f 0%, ${topic.accent}05 100%)`, borderColor: `${topic.accent}55` }}
        >
          <Sparkles size={22} className="mx-auto mb-3" style={{ color: topic.accent }} />
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            Ready to earn your first belt?
          </h2>
          <p className="text-[var(--ink-2)] mt-3 max-w-lg mx-auto">
            Find your level, choose your identity, and start training.
          </p>
          <Link to="/skill-check" className="btn-duo mt-6 inline-flex" data-testid="topic-cta-signup">
            Start My Skill Check <ArrowRight size={14} />
          </Link>
        </div>
      </main>
    </div>
  );
}
